import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchPipelineHealth } from "../lib/api";
import { ammanStamp } from "../lib/dates";
import { roleCan, useRole } from "../lib/roles";
import { PageSkeleton } from "../components/PageSkeleton";
import { EmptyState, PageHeader } from "../components/primitives";
import { AccessDenied } from "./NewsletterEditorPage";

const nf = new Intl.NumberFormat("en-US");

export function PipelinePage() {
  const { role } = useRole();
  const q = useQuery({ queryKey: ["pipeline"], queryFn: fetchPipelineHealth });
  if (!roleCan(role, "pipeline.view")) return <AccessDenied need="Admins" />;
  if (q.isLoading) return <PageSkeleton />;
  const d = q.data;

  return (
    <div style={{ display: "grid", gap: "var(--ui-space-5)" }}>
      <PageHeader
        title="Member pipeline"
        meta={d?.last_sync_success ? <>Last member sync · {ammanStamp(d.last_sync_success)}</> : "How Altibbi accounts reach the marketing warehouse"}
      />
      <p className="bc-hint pl-intro">
        These are all Altibbi accounts, not newsletter subscribers. Subscribers are on the Subscribers and Audience pages,
        and newsletter sending is in <Link to="/broadcasts">Broadcasts</Link>.
      </p>

      {q.isError || !d ? (
        <EmptyState title="The pipeline could not be loaded" body={(q.error as Error | null)?.message ?? "No data returned."} hint="Reload the page to try again." />
      ) : (
        <>
          <section aria-label="Altibbi members">
            <h2 className="nl-panel-title an-title">Altibbi members</h2>
            <dl className="bc-stats pl-stats">
              <Stat label="Accounts" value={d.profiles} />
              <Stat label="Marketing consent given" value={d.consent_granted} />
              <Stat label="No consent recorded" value={d.consent_missing} />
              <Stat label="Chose interests" value={d.explicit_interest} />
            </dl>
          </section>

          <section aria-label="Customer.io welcome pool">
            <h2 className="nl-panel-title an-title">Customer.io welcome pool</h2>
            <dl className="bc-stats pl-stats">
              <Stat label="Waiting" value={d.cio_waiting ?? 0} />
              <Stat label="Active" value={d.cio_active ?? 0} />
              <Stat label="Removed" value={d.cio_removed ?? 0} />
              {d.profile_events != null && <Stat label="Profile events" value={d.profile_events} />}
            </dl>
          </section>

          {d.last_sync_error && (
            <p className="bc-error" role="alert">Last sync error: {d.last_sync_error}</p>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="bc-stat">
      <dt>{label}</dt>
      <dd className="tabnum">{nf.format(value)}</dd>
    </div>
  );
}
